<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { FirebaseError } from 'firebase/app';
import { useRoute } from 'vue-router';
import { signupCm } from '@/api';
import { recaptchaHeaders } from '@/utils/recaptcha';
import { logger } from '@/utils/logger';
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaAuthStatus from '@/components/design/domain/auth/GaiaAuthStatus.vue';
import GaiaGoogleButton from '@/components/design/domain/auth/GaiaGoogleButton.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';

const route = useRoute();

const email = ref<string>('');
const error = ref<string | null>(null);
const loading = ref<boolean>(false);
const submitting = ref<boolean>(false);
const currentStep = ref<number>(1);

// Form values - Step 2
const fullName = ref<string>('');
const companyName = ref<string>('');
const contractTypes = ref<string[]>([]);
const products = ref<string[]>([]);
const companySize = ref<string>('');

// Options for select fields
const contractTypeOptions = [
  { label: 'Commercial', value: 'commercial' },
  { label: 'Financial', value: 'financial' },
  { label: 'HR', value: 'hr' },
  { label: 'Corporate', value: 'corporate' },
];

const productOptions = [
  { label: 'Equity Management', value: 'equity_management' },
  { label: 'Contract Management', value: 'contract_management' },
  { label: 'AI Risk Reports', value: 'ai_risk_reports' },
];

const companySizeOptions = [
  { label: '1-10 employees', value: '1-10' },
  { label: '11-50 employees', value: '11-50' },
  { label: '51-200 employees', value: '51-200' },
  { label: '201-500 employees', value: '201-500' },
  { label: '501+ employees', value: '501+' },
];

onMounted((): void => {});

function handleEmailSignUp(): void {
  if (!email.value) {
    error.value = 'Please enter your email';
    return;
  }

  // Simple email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    error.value = 'Please enter a valid email address';
    return;
  }

  currentStep.value = 2;
}

/**
 * Handle Google sign-up using direct Firebase methods with no backend connection
 */
const handleGoogleSignUp = async (): Promise<void> => {
  try {
    loading.value = true;
    error.value = '';

    // Create Google auth provider
    const provider = new GoogleAuthProvider();

    // Sign in with popup
    const result = await signInWithPopup(getFirebaseAuth(), provider);

    // Get user info from result
    const user = result.user;

    // Successfully signed in, move to step 2
    email.value = user.email ?? '';
    currentStep.value = 2;
  } catch (error_: unknown) {
    logger.error('Google sign-up error:', error_);

    // Handle Firebase-specific errors
    if (error_ instanceof FirebaseError) {
      switch (error_.code) {
        case 'auth/popup-closed-by-user':
          error.value = 'Sign-up was cancelled. Please try again.';
          break;
        case 'auth/network-request-failed':
          error.value = 'Network error. Please check your internet connection.';
          break;
        case 'auth/too-many-requests':
          error.value = 'Too many attempts. Please try again later.';
          break;
        default:
          error.value = 'Error during Google sign-up. Please try again.';
      }
    } else {
      error.value = 'Error during Google sign-up. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};

/** Both chip groups are multi-select, so a tap flips membership. */
function toggleIn(list: string[], value: string): void {
  const index = list.indexOf(value);
  if (index === -1) list.push(value);
  else list.splice(index, 1);
}

async function handleSubmit(): Promise<void> {
  // Validate form
  if (!fullName.value || !companyName.value) {
    error.value = 'Please fill in all required fields';
    return;
  }

  if (contractTypes.value.length === 0) {
    error.value = 'Please select at least one contract type';
    return;
  }

  if (products.value.length === 0) {
    error.value = 'Please select at least one product';
    return;
  }

  if (!companySize.value) {
    error.value = 'Please select your company size';
    return;
  }

  submitting.value = true;
  error.value = null;

  try {
    // Prepare signup data
    const signupData = {
      email: email.value,
      full_name: fullName.value,
      company_name: companyName.value,
      contracts_used: contractTypes.value,
      products_interested_in: products.value,
      company_size: companySize.value,
      referrer: route.query.referrer ? (route.query.referrer as string) : null,
    };

    const { response } = await signupCm({
      body: signupData,
      headers: await recaptchaHeaders('signup_cm'),
      throwOnError: false,
    });

    if (!response?.ok) {
      throw new Error(`API request failed with status ${response?.status}`);
    }

    // Redirect to Stripe checkout page regardless of API response
    window.location.href = 'https://buy.stripe.com/00gdSCblN8IHayI28d';
  } catch (error_) {
    logger.error('Form submission error:', error_);
    // Even if the API call fails, redirect to Stripe
    // This ensures users can still complete the payment process
    window.location.href = 'https://buy.stripe.com/00gdSCblN8IHayI28d';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <gaia-auth-card
    width="420px"
    :title="currentStep === 1 ? 'Sign up' : 'Complete your profile'"
    :subtitle="
      currentStep === 1
        ? 'Get started with a 7-day free trial.'
        : 'A few details before your trial starts.'
    "
  >
    <!-- The step counter sits above the title in the mock; GaiaAuthCard has no
         slot above its heading, so it rides the #eyebrow slot. -->
    <template #eyebrow>Step {{ currentStep }} of 2</template>

    <!-- Step 1: email, or Google straight into step 2 -->
    <template v-if="currentStep === 1">
      <n-flex vertical :size="16">
        <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
        <gaia-form-field label="Email" variant="uppercase">
          <gaia-input
            :value="email"
            size="large"
            placeholder="you@firm.com"
            :disabled="loading"
            :input-props="{ type: 'email', autocomplete: 'email' }"
            style="width: 100%"
            @update:value="(v: string) => (email = v.toLowerCase())"
            @keydown.enter="handleEmailSignUp"
          />
        </gaia-form-field>
      </n-flex>

      <gaia-button
        type="primary"
        size="large"
        block
        :style="{ marginTop: '22px' }"
        @click="handleEmailSignUp"
      >
        Continue with email
      </gaia-button>

      <gaia-divider variant="subtle">or</gaia-divider>
      <gaia-google-button :disabled="loading" @click="handleGoogleSignUp">
        Sign up with Google
      </gaia-google-button>
    </template>

    <!-- Step 2: the profile, then straight to Stripe -->
    <template v-else>
      <gaia-auth-status v-if="submitting" loading>Taking you to checkout…</gaia-auth-status>

      <template v-else>
        <n-flex vertical :size="18">
          <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>

          <gaia-form-field label="Full name" variant="uppercase" required>
            <gaia-input
              v-model:value="fullName"
              size="large"
              placeholder="Sarah Lindqvist"
              :input-props="{ autocomplete: 'name' }"
              style="width: 100%"
            />
          </gaia-form-field>

          <gaia-form-field label="Company name" variant="uppercase" required>
            <gaia-input
              v-model:value="companyName"
              size="large"
              placeholder="Lindqvist & Partners"
              :input-props="{ autocomplete: 'organization' }"
              style="width: 100%"
            />
          </gaia-form-field>

          <gaia-form-field label="Which contracts do you use?" variant="uppercase">
            <n-flex :size="7">
              <gaia-tag
                v-for="option in contractTypeOptions"
                :key="option.value"
                pill
                checkable
                size="medium"
                :checked="contractTypes.includes(option.value)"
                @update:checked="toggleIn(contractTypes, option.value)"
              >
                {{ option.label }}
              </gaia-tag>
            </n-flex>
          </gaia-form-field>

          <gaia-form-field label="Which products do you need?" variant="uppercase">
            <n-flex :size="7">
              <gaia-tag
                v-for="option in productOptions"
                :key="option.value"
                pill
                checkable
                size="medium"
                :checked="products.includes(option.value)"
                @update:checked="toggleIn(products, option.value)"
              >
                {{ option.label }}
              </gaia-tag>
            </n-flex>
          </gaia-form-field>

          <gaia-form-field label="Company size" variant="uppercase">
            <gaia-segmented
              v-model:value="companySize"
              block
              size="medium"
              :options="companySizeOptions.map(o => ({ value: o.value, label: o.value }))"
            />
          </gaia-form-field>
        </n-flex>

        <gaia-button
          type="primary"
          size="large"
          block
          :loading="submitting"
          :style="{ marginTop: '24px' }"
          @click="handleSubmit"
        >
          Complete sign up
        </gaia-button>
      </template>
    </template>

    <template #footer>
      By signing up you agree to our
      <gaia-link href="https://www.gaia.law/tos" target="_blank">terms and conditions</gaia-link>
      and
      <gaia-link href="https://www.gaia.law/privacy" target="_blank">privacy policy</gaia-link>.
    </template>
  </gaia-auth-card>
</template>
